/**
 * Cardano API Service
 *
 * Provides access to Cardano account data via Blockfrost API.
 * Free tier: 50,000 requests/day
 *
 * Documentation: https://docs.blockfrost.io/
 */

import { logger } from './logger';
import { callCoinGeckoAPI } from './api';
import type {
  CardanoAccount,
  CardanoAddress,
  CardanoTransaction,
  CardanoPool,
  CardanoPoolMetadata,
  CardanoReward,
  CardanoAsset,
  CardanoWalletData,
  CardanoAssetWithInfo,
} from '../types/cardano.types';

// Blockfrost API (mainnet) - uses public project for demo
// In production, user should provide their own API key
const BLOCKFROST_URL = 'https://cardano-mainnet.blockfrost.io/api/v0';

// Lovelace to ADA conversion (1 ADA = 1,000,000 lovelace)
const LOVELACE_TO_ADA = 1_000_000;

// Request timeout in ms
const REQUEST_TIMEOUT_MS = 15000;

// Rate limiting
const REQUEST_DELAY_MS = 100;

// Cache for pool info
const poolInfoCache = new Map<string, CardanoPoolMetadata>();
const assetInfoCache = new Map<string, CardanoAsset>();

/**
 * Delay helper for rate limiting
 */
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Get Blockfrost API key from env or use demo key
 */
const getApiKey = (): string => {
  // Try to get from environment
  if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_BLOCKFROST_API_KEY) {
    return (import.meta as any).env.VITE_BLOCKFROST_API_KEY;
  }
  // Demo project ID (limited requests)
  return 'mainnetd7dFVVnVFZm7QqAjvSQvb7P3mvcWYSzw';
};

/**
 * Fetch with timeout and auth
 */
const fetchWithAuth = async (url: string): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        'project_id': getApiKey(),
      }
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

/**
 * Convert lovelace to ADA
 */
export const lovelaceToAda = (lovelace: string | number): number => {
  return Number(lovelace) / LOVELACE_TO_ADA;
};

/**
 * Validate Cardano address format
 */
export const isValidCardanoAddress = (address: string): boolean => {
  // Shelley addresses start with addr1
  // Stake addresses start with stake1
  return /^(addr1|stake1)[a-z0-9]{50,}$/.test(address);
};

/**
 * Cardano API Service
 */
export const cardanoAPI = {
  /**
   * Get address information
   */
  async getAddress(address: string): Promise<CardanoAddress | null> {
    try {
      if (!isValidCardanoAddress(address)) {
        logger.warn('CardanoAPI', `Invalid address format: ${address}`);
        return null;
      }

      const response = await fetchWithAuth(`${BLOCKFROST_URL}/addresses/${address}`);

      if (!response.ok) {
        if (response.status === 404) {
          logger.warn('CardanoAPI', `Address not found: ${address}`);
          return null;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('CardanoAPI', `Failed to fetch address ${address}`, error);
      throw error;
    }
  },

  /**
   * Get stake account information
   */
  async getStakeAccount(stakeAddress: string): Promise<CardanoAccount | null> {
    try {
      const response = await fetchWithAuth(`${BLOCKFROST_URL}/accounts/${stakeAddress}`);

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('CardanoAPI', `Failed to fetch stake account ${stakeAddress}`, error);
      return null;
    }
  },

  /**
   * Get address transactions
   */
  async getTransactions(
    address: string,
    limit: number = 50
  ): Promise<CardanoTransaction[]> {
    try {
      const response = await fetchWithAuth(
        `${BLOCKFROST_URL}/addresses/${address}/transactions?count=${limit}&order=desc`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const txHashes: { tx_hash: string }[] = await response.json();

      // Fetch transaction details for each hash (limited to first 10 for performance)
      const transactions: CardanoTransaction[] = [];
      for (const { tx_hash } of txHashes.slice(0, 10)) {
        const txResponse = await fetchWithAuth(`${BLOCKFROST_URL}/txs/${tx_hash}`);
        if (txResponse.ok) {
          transactions.push(await txResponse.json());
        }
        await delay(REQUEST_DELAY_MS);
      }

      return transactions;
    } catch (error) {
      logger.error('CardanoAPI', `Failed to fetch transactions for ${address}`, error);
      return [];
    }
  },

  /**
   * Get account rewards history
   */
  async getRewards(stakeAddress: string): Promise<CardanoReward[]> {
    try {
      const response = await fetchWithAuth(
        `${BLOCKFROST_URL}/accounts/${stakeAddress}/rewards?count=100&order=desc`
      );

      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('CardanoAPI', `Failed to fetch rewards for ${stakeAddress}`, error);
      return [];
    }
  },

  /**
   * Get pool metadata
   */
  async getPoolMetadata(poolId: string): Promise<CardanoPoolMetadata | null> {
    // Check cache first
    if (poolInfoCache.has(poolId)) {
      return poolInfoCache.get(poolId)!;
    }

    try {
      const response = await fetchWithAuth(`${BLOCKFROST_URL}/pools/${poolId}/metadata`);

      if (!response.ok) {
        return null;
      }

      const metadata: CardanoPoolMetadata = await response.json();
      poolInfoCache.set(poolId, metadata);

      return metadata;
    } catch (error) {
      logger.error('CardanoAPI', `Failed to fetch pool metadata for ${poolId}`, error);
      return null;
    }
  },

  /**
   * Get asset information
   */
  async getAssetInfo(asset: string): Promise<CardanoAsset | null> {
    // Check cache first
    if (assetInfoCache.has(asset)) {
      return assetInfoCache.get(asset)!;
    }

    try {
      const response = await fetchWithAuth(`${BLOCKFROST_URL}/assets/${asset}`);

      if (!response.ok) {
        return null;
      }

      const assetInfo: CardanoAsset = await response.json();
      assetInfoCache.set(asset, assetInfo);

      return assetInfo;
    } catch (error) {
      logger.error('CardanoAPI', `Failed to fetch asset info for ${asset}`, error);
      return null;
    }
  },

  /**
   * Get ADA price from CoinGecko
   */
  async getAdaPrice(): Promise<number> {
    try {
      const data = await callCoinGeckoAPI('/simple/price', {
        ids: 'cardano',
        vs_currencies: 'usd'
      });
      return data['cardano']?.usd || 0;
    } catch (error) {
      logger.error('CardanoAPI', 'Failed to fetch ADA price', error);
      return 0;
    }
  },

  /**
   * Get complete wallet data for an address
   */
  async getWalletData(address: string, adaPrice: number): Promise<CardanoWalletData | null> {
    try {
      // Fetch address info
      const addressInfo = await this.getAddress(address);
      if (!addressInfo) {
        return null;
      }

      await delay(REQUEST_DELAY_MS);

      // Calculate balance from amounts
      const lovelaceAmount = addressInfo.amount.find(a => a.unit === 'lovelace');
      const balance = lovelaceAmount ? lovelaceToAda(lovelaceAmount.quantity) : 0;

      // Fetch stake account if exists
      let stakeAccount: CardanoAccount | null = null;
      let poolMetadata: CardanoPoolMetadata | null = null;

      if (addressInfo.stake_address) {
        stakeAccount = await this.getStakeAccount(addressInfo.stake_address);
        await delay(REQUEST_DELAY_MS);

        if (stakeAccount?.pool_id) {
          poolMetadata = await this.getPoolMetadata(stakeAccount.pool_id);
          await delay(REQUEST_DELAY_MS);
        }
      }

      // Fetch transactions
      const transactions = await this.getTransactions(address, 25);
      await delay(REQUEST_DELAY_MS);

      // Process assets
      const assetsWithInfo: CardanoAssetWithInfo[] = [];
      const nativeAssets = addressInfo.amount.filter(a => a.unit !== 'lovelace').slice(0, 20);

      for (const asset of nativeAssets) {
        const assetInfo = await this.getAssetInfo(asset.unit);
        if (assetInfo) {
          const decimals = assetInfo.metadata?.decimals || 0;
          assetsWithInfo.push({
            asset: asset.unit,
            policyId: assetInfo.policy_id,
            assetName: assetInfo.asset_name || '',
            fingerprint: assetInfo.fingerprint,
            quantity: Number(asset.quantity) / Math.pow(10, decimals),
            decimals,
            name: assetInfo.metadata?.name || assetInfo.onchain_metadata?.name || 'Unknown',
            ticker: assetInfo.metadata?.ticker || null,
          });
        }
        await delay(REQUEST_DELAY_MS);
      }

      // Calculate staking values
      const stakedAmount = stakeAccount ? lovelaceToAda(stakeAccount.controlled_amount) : 0;
      const withdrawableRewards = stakeAccount ? lovelaceToAda(stakeAccount.withdrawable_amount) : 0;
      const totalRewards = stakeAccount ? lovelaceToAda(stakeAccount.rewards_sum) : 0;

      return {
        address,
        stakeAddress: addressInfo.stake_address,
        balance,
        balanceUsd: balance * adaPrice,
        stakedAmount,
        stakedAmountUsd: stakedAmount * adaPrice,
        withdrawableRewards,
        withdrawableRewardsUsd: withdrawableRewards * adaPrice,
        totalRewards,
        totalRewardsUsd: totalRewards * adaPrice,
        poolId: stakeAccount?.pool_id || null,
        poolName: poolMetadata?.name || null,
        poolTicker: poolMetadata?.ticker || null,
        isStaking: !!stakeAccount?.pool_id,
        assets: assetsWithInfo,
        recentTransactions: transactions,
        lastUpdated: Date.now(),
      };
    } catch (error) {
      logger.error('CardanoAPI', `Failed to get wallet data for ${address}`, error);
      return null;
    }
  },

  /**
   * Get aggregated data for multiple addresses
   */
  async getMultipleWalletsData(
    addresses: string[],
    adaPrice: number
  ): Promise<CardanoWalletData[]> {
    const results: CardanoWalletData[] = [];

    for (const address of addresses) {
      const walletData = await this.getWalletData(address, adaPrice);
      if (walletData) {
        results.push(walletData);
      }
      await delay(500);
    }

    return results;
  },

  /**
   * Format timestamp for display
   */
  formatTimestamp(blockTime: number): Date {
    return new Date(blockTime * 1000);
  }
};

export default cardanoAPI;
